import { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { Stack } from 'react-bootstrap';
import ImageWithErrorHandling from '../../../components/image/ImageWithErrorHandling';
import ConfirmationModal from '../../../components/modals/ConfirmationModal';
import { DateCell } from '../../../components/tables/cellComponents/DateCell';
import { LinkCell } from '../../../components/tables/cellComponents/LinkCell';
import YesNoCell from '../../../components/tables/cellComponents/YesNoCell';
import { useToast } from '../../../context/ToastContext';
import { useDeleteFeedbackMutation } from '../../../services/endpoints/website/feedback';
import { useDeleteFranchiseeLeadMutation } from '../../../services/endpoints/website/franchiseeLead';
import { useDeleteGalleryMutation } from '../../../services/endpoints/website/gallery';
import { useDeleteNewsItemMutation } from '../../../services/endpoints/website/newsItem';
import { useDeletePageMutation } from '../../../services/endpoints/website/page';
import { useDeleteSnippetMutation } from '../../../services/endpoints/website/snippet';
import { useDeleteTeamProfileMutation } from '../../../services/endpoints/website/teamProfile';
import { getCurrentUser } from '../../../services/helper';
import {
  Feedback,
  FranchiseeLead,
  Gallery,
  NewsItem,
  Page,
  Snippet,
  TeamProfile
} from '../../../types/website.types';
import {
  canEditFeedbacks,
  canEditGalleries,
  canEditNewsItems,
  canEditPages,
  canEditSnippets,
  canEditTeamProfiles
} from '../../../utils/rbac';
import { getErrorMessage, getPartition } from '../../utils';

const DeleteNewsItemButton: React.FC<{ id: number }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [deleteNewsItem, { isLoading }] = useDeleteNewsItemMutation();
  const { addToast } = useToast();

  const handleDelete = async () => {
    try {
      await deleteNewsItem(id).unwrap();
      addToast('News item deleted successfully', 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <button
        className="text-info fw-bolder btn btn-link p-0"
        style={{ fontSize: '.875rem', textDecoration: 'underline' }}
        onClick={() => setShowModal(true)}
      >
        Delete
      </button>
      <ConfirmationModal
        show={showModal}
        title="Delete News Item"
        message="Are you sure you want to delete this news item?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isLoading}
      />
    </>
  );
};

const DeletePageButton: React.FC<{ id: number }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [deletePage, { isLoading }] = useDeletePageMutation();
  const { addToast } = useToast();

  const handleDelete = async () => {
    try {
      await deletePage(id).unwrap();
      addToast('Page deleted successfully', 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <button
        className="text-info fw-bolder btn btn-link p-0"
        style={{ fontSize: '.875rem', textDecoration: 'underline' }}
        onClick={() => setShowModal(true)}
      >
        Delete
      </button>
      <ConfirmationModal
        show={showModal}
        title="Delete Page"
        message="Are you sure you want to delete this page?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isLoading}
      />
    </>
  );
};

const DeleteSnippetButton: React.FC<{ id: number }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [deleteSnippet, { isLoading }] = useDeleteSnippetMutation();
  const { addToast } = useToast();

  const handleDelete = async () => {
    try {
      await deleteSnippet(id).unwrap();
      addToast('Snippet deleted successfully', 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <button
        className="text-info fw-bolder btn btn-link p-0"
        style={{ fontSize: '.875rem', textDecoration: 'underline' }}
        onClick={() => setShowModal(true)}
      >
        Delete
      </button>
      <ConfirmationModal
        show={showModal}
        title="Delete Snippet"
        message="Are you sure you want to delete this snippet?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isLoading}
      />
    </>
  );
};

const DeleteTeamProfileButton: React.FC<{ id: number }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [deleteTeamProfile, { isLoading }] = useDeleteTeamProfileMutation();
  const { addToast } = useToast();

  const handleDelete = async () => {
    try {
      await deleteTeamProfile(id).unwrap();
      addToast('Team profile deleted successfully', 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <button
        className="text-info fw-bolder btn btn-link p-0"
        style={{ fontSize: '.875rem', textDecoration: 'underline' }}
        onClick={() => setShowModal(true)}
      >
        Delete
      </button>
      <ConfirmationModal
        show={showModal}
        title="Delete Team Profile"
        message="Are you sure you want to delete this team profile?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isLoading}
      />
    </>
  );
};

const DeleteFeedbackButton: React.FC<{ id: number }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [deleteFeedback, { isLoading }] = useDeleteFeedbackMutation();
  const { addToast } = useToast();

  const handleDelete = async () => {
    try {
      await deleteFeedback(id).unwrap();
      addToast('Feedback deleted successfully', 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <button
        className="text-info fw-bolder btn btn-link p-0"
        style={{ fontSize: '.875rem', textDecoration: 'underline' }}
        onClick={() => setShowModal(true)}
      >
        Delete
      </button>
      <ConfirmationModal
        show={showModal}
        title="Delete Feedback"
        message="Are you sure you want to delete this feedback?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isLoading}
      />
    </>
  );
};

const DeleteGalleryButton: React.FC<{ id: number }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [deleteGallery, { isLoading }] = useDeleteGalleryMutation();
  const { addToast } = useToast();

  const handleDelete = async () => {
    try {
      await deleteGallery(id).unwrap();
      addToast('Gallery deleted successfully', 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <button
        className="text-info fw-bolder btn btn-link p-0"
        style={{ fontSize: '.875rem', textDecoration: 'underline' }}
        onClick={() => setShowModal(true)}
      >
        Delete
      </button>
      <ConfirmationModal
        show={showModal}
        title="Delete Gallery"
        message="Are you sure you want to delete this gallery?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isLoading}
      />
    </>
  );
};

const currentUser = getCurrentUser();

export const newsItemColumns: ColumnDef<NewsItem>[] = [
  {
    header: 'ID',
    accessorKey: 'id',
    cell: ({ getValue }) => (
      <LinkCell
        content={getValue<number>()}
        url={`/admin/website/news-items/${getValue<number>()}`}
      />
    )
  },
  {
    header: 'Image',
    accessorKey: 'image_file_name',
    cell: ({ row }) => {
      let resource_id = getPartition(row.original.id);
      let url = `${row.original.s3_base_url ?? ''}/${resource_id}/${row.original.image_file_name}`;
      let content = (
        <ImageWithErrorHandling
          src={url ?? ''}
          alt={row.original.image_file_name}
        />
      );
      return (
        <LinkCell
          content={content}
          url={`/admin/website/news-items/${row.original.id}`}
        />
      );
    }
  },
  {
    header: 'Name',
    accessorKey: 'name'
  },
  {
    header: 'Published On',
    accessorKey: 'published_on',
    cell: ({ getValue }) => (
      <DateCell
        date={getValue<string>()}
        format={'MM/dd/yyyy'}
      />
    )
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/website/news-items/${row.original.id}`}
          underline
        />

        {canEditNewsItems(currentUser) && (
          <>
            <LinkCell
              content="Edit"
              url={`/admin/website/news-items/${row.original.id}/edit`}
              underline
            />
            <DeleteNewsItemButton id={row.original.id!} />
          </>
        )}
      </Stack>
    )
  }
];

export const pageColumns: ColumnDef<Page>[] = [
  {
    header: 'ID',
    accessorKey: 'id',
    cell: ({ getValue }) => (
      <LinkCell
        content={getValue<number>()}
        url={`/admin/website/pages/${getValue<number>()}`}
      />
    )
  },
  {
    header: 'Name',
    accessorKey: 'name'
  },
  {
    header: 'Image',
    accessorKey: 'image_file_name',
    enableSorting: false,
    cell: ({ row }) => {
      let resource_id = getPartition(row.original.id);
      let url = `${row.original.s3_base_url ?? ''}/${resource_id}/thumb/${row.original.image_file_name}`;
      let content = (
        <ImageWithErrorHandling
          src={url ?? ''}
          alt={row.original.image_file_name}
        />
      );
      return (
        <LinkCell
          content={content}
          url={`/admin/website/pages/${row.original.id}`}
        />
      );
    }
  },
  {
    header: 'Slug',
    accessorKey: 'slug'
  },
  {
    header: 'Is Featured',
    accessorKey: 'is_featured',
    cell: ({ getValue }) => <YesNoCell value={getValue<boolean>()} />
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/website/pages/${row.original.id}`}
          underline
        />

        {canEditPages(currentUser) && (
          <>
            <LinkCell
              content="Edit"
              url={`/admin/website/pages/${row.original.id}/edit`}
              underline
            />
            <DeletePageButton id={row.original.id!} />
          </>
        )}
      </Stack>
    )
  }
];

export const snippetColumns: ColumnDef<Snippet>[] = [
  {
    header: 'Name',
    accessorKey: 'name'
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/website/snippets/${row.original.id}`}
          underline
        />
        {canEditSnippets(currentUser) && (
          <>
            <LinkCell
              content="Edit"
              url={`/admin/website/snippets/${row.original.id}/edit`}
              underline
            />
            <DeleteSnippetButton id={row.original.id!} />
          </>
        )}
      </Stack>
    )
  }
];

export const teamProfileColumns: ColumnDef<TeamProfile>[] = [
  {
    header: 'ID',
    accessorKey: 'id',
    cell: ({ getValue }) => (
      <LinkCell
        content={getValue<number>()}
        url={`/admin/website/team-profiles/${getValue<number>()}`}
      />
    )
  },
  {
    header: 'Image',
    accessorKey: 'image_file_name',
    cell: ({ row }) => {
      // s3_base_url already includes partition + /medium; prefer signed_url from list API
      const signedUrl = (row.original as TeamProfile & { signed_url?: string }).signed_url;
      const url =
        signedUrl ||
        (row.original.s3_base_url && row.original.image_file_name
          ? `${row.original.s3_base_url}/${row.original.image_file_name}`
          : '');
      if (!url) return null;
      return (
        <LinkCell
          content={
            <img
              src={url}
              alt={row.original.image_file_name ?? ''}
              style={{
                width: 48,
                height: 48,
                maxHeight: 48,
                objectFit: 'cover',
                borderRadius: 4,
                display: 'block'
              }}
            />
          }
          url={`/admin/website/team-profiles/${row.original.id}`}
        />
      );
    }
  },
  {
    header: 'Name',
    accessorKey: 'name'
  },
  {
    header: 'Title',
    accessorKey: 'title'
  },
  {
    header: 'Location',
    accessorKey: 'location_id',
    cell: ({ row }) => (
      <LinkCell
        content={row.original.location?.title}
        url={`/admin/places/locations/${row.original.location_id}`}
      />
    )
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/website/team-profiles/${row.original.id}`}
          underline
        />

        {canEditTeamProfiles(currentUser) && (
          <>
            <LinkCell
              content="Edit"
              url={`/admin/website/team-profiles/${row.original.id}/edit`}
              underline
            />
            <DeleteTeamProfileButton id={row.original.id!} />
          </>
        )}
      </Stack>
    )
  }
];

export const feedbackColumns: ColumnDef<Feedback>[] = [
  {
    header: 'ID',
    accessorKey: 'id',
    cell: ({ getValue }) => (
      <LinkCell
        content={getValue<number>()}
        url={`/admin/website/feedbacks/${getValue<number>()}`}
      />
    )
  },
  {
    header: 'First Name',
    accessorKey: 'first_name'
  },
  {
    header: 'Last Name',
    accessorKey: 'last_name'
  },
  {
    header: 'Location',
    accessorKey: 'location'
  },
  {
    header: 'Email',
    accessorKey: 'email'
  },
  {
    header: 'Phone Number',
    accessorKey: 'phone_number'
  },
  {
    header: 'Message',
    accessorKey: 'message'
  },
  {
    header: 'Created At',
    accessorKey: 'created_at',
    cell: ({ getValue }) => (
      <DateCell
        date={getValue<string>()}
        format={'MM/dd/yyyy'}
      />
    )
  },
  {
    header: 'Updated At',
    accessorKey: 'updated_at',
    cell: ({ getValue }) => (
      <DateCell
        date={getValue<string>()}
        format={'MM/dd/yyyy'}
      />
    )
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/website/feedbacks/${row.original.id}`}
          underline
        />

        {canEditFeedbacks(currentUser) && (
          <>
            <LinkCell
              content="Edit"
              url={`/admin/website/feedbacks/${row.original.id}/edit`}
              underline
            />
            <DeleteFeedbackButton id={row.original.id!} />
          </>
        )}
      </Stack>
    )
  }
];

const DeleteFranchiseeLeadButton: React.FC<{ id: number }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [deleteLead, { isLoading }] = useDeleteFranchiseeLeadMutation();
  const { addToast } = useToast();

  const handleDelete = async () => {
    try {
      await deleteLead(id).unwrap();
      addToast('Franchisee lead deleted successfully', 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <button
        className="text-info fw-bolder btn btn-link p-0"
        style={{ fontSize: '.875rem', textDecoration: 'underline' }}
        onClick={() => setShowModal(true)}
      >
        Delete
      </button>
      <ConfirmationModal
        show={showModal}
        title="Delete Franchisee Lead"
        message="Are you sure you want to delete this franchisee lead?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isLoading}
      />
    </>
  );
};

export const franchiseeLeadsColumns: ColumnDef<FranchiseeLead>[] = [
  {
    header: 'ID',
    accessorKey: 'id',
    cell: ({ getValue }) => (
      <LinkCell
        content={getValue<number>()}
        url={`/admin/website/franchisee-leads/${getValue<number>()}`}
      />
    )
  },
  {
    header: 'Name',
    accessorKey: 'name'
  },
  {
    header: 'Email',
    accessorKey: 'email'
  },
  {
    header: 'Phone Number',
    accessorKey: 'phone_number'
  },
  {
    header: 'Region',
    accessorKey: 'region'
  },
  {
    header: 'Created At',
    accessorKey: 'created_at',
    cell: ({ getValue }) => (
      <DateCell
        date={getValue<string>()}
        format={'MM/dd/yyyy'}
      />
    )
  },
  {
    header: 'Updated At',
    accessorKey: 'updated_at',
    cell: ({ getValue }) => (
      <DateCell
        date={getValue<string>()}
        format={'MM/dd/yyyy'}
      />
    )
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/website/franchisee-leads/${row.original.id}`}
          underline
        />
        <LinkCell
          content="Edit"
          url={`/admin/website/franchisee-leads/${row.original.id}/edit`}
          underline
        />
        <DeleteFranchiseeLeadButton id={row.original.id!} />
      </Stack>
    )
  }
];

export const galleryColumns: ColumnDef<Gallery>[] = [
  {
    header: 'ID',
    accessorKey: 'id',
    cell: ({ getValue }) => (
      <LinkCell
        content={getValue<number>()}
        url={`/admin/website/galleries/${getValue<number>()}`}
      />
    )
  },
  {
    header: 'Name',
    accessorKey: 'name'
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/website/galleries/${row.original.id}`}
          underline
        />

        {canEditGalleries(currentUser) && (
          <>
            <LinkCell
              content="Edit"
              url={`/admin/website/galleries/${row.original.id}/edit`}
              underline
            />
            <DeleteGalleryButton id={row.original.id!} />
          </>
        )}
      </Stack>
    )
  }
];
